/**
 * ThemeToggle Component
 *
 * Compact three-option selector (Light / Dark / System) using small
 * pill-shaped chips. The active mode gets a subtle highlight.
 *
 * Requirements: 1.6, 10.3, 10.5
 */

import { Box, Chip } from '@mui/material';
import { LightMode, DarkMode, SettingsBrightness } from '@mui/icons-material';
import { useThemeMode, type ThemeMode } from '../contexts/ThemeContext';

const options: { value: ThemeMode; label: string; icon: React.ReactElement }[] = [
    { value: 'light', label: 'Light', icon: <LightMode sx={{ fontSize: 14 }} /> },
    { value: 'dark', label: 'Dark', icon: <DarkMode sx={{ fontSize: 14 }} /> },
    { value: 'system', label: 'System', icon: <SettingsBrightness sx={{ fontSize: 14 }} /> },
];

export function ThemeToggle() {
    const { mode, setMode } = useThemeMode();

    return (
        <Box sx={{ display: 'flex', gap: 0.75 }} role="radiogroup" aria-label="Theme mode">
            {options.map((opt) => (
                <Chip
                    key={opt.value}
                    icon={opt.icon}
                    label={opt.label}
                    size="small"
                    variant={mode === opt.value ? 'filled' : 'outlined'}
                    color={mode === opt.value ? 'primary' : 'default'}
                    onClick={() => setMode(opt.value)}
                    aria-label={`${opt.label} mode`}
                    aria-checked={mode === opt.value}
                    role="radio"
                    sx={{
                        cursor: 'pointer',
                        fontWeight: mode === opt.value ? 600 : 400,
                        fontSize: '0.75rem',
                        height: 26,
                        '& .MuiChip-icon': { fontSize: 14 },
                    }}
                />
            ))}
        </Box>
    );
}
